import { Fragment, useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router";
import { Box } from "@mui/material";
import PowerBIReport from "components/PowerBIReport/PowerBIReport";
import ReportFiltersMenu from "./components/ReportFiltersMenu";
import ReportToolbar from "./components/ReportToolbar";
import styles from "./Graphs.module.scss";
import { useDispatch } from "react-redux";
import { setSaveTemplateRequestStatus } from "store/templates";
import { RequestStatus } from "constants/RequestStatus";
import { selectSaveTemplateRequestStatus } from "store/templates/selectors";
import { useAppSelector } from "utils/hooks/storeHooks";
import { Report, models } from "powerbi-client";
import Toast from "components/Toast/Toast";
import { ToastVariant } from "constants/ToastVariant";

const Graphs = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const saveTemplateRequestStatus = useAppSelector(
    selectSaveTemplateRequestStatus
  );
  const [isFiltersMenuOpen, setIsFiltersMenuOpen] = useState<boolean>(false);
  const [isReportLoaded, setIsReportLoaded] = useState<boolean>(false);
  const isViewMode = Boolean(location.state.viewMode);
  const [initialFiltersArr, setInitialFiltersArr] = useState<models.IFilter[]>(
    []
  );
  const [reportPage, setReportPage] = useState<number>(0);
  const [reportRef, setReportRef] = useState<Report>();
  const [reportState, setReportState] = useState<string>("");
  const [showSnackBar, setShowSnackbar] = useState<boolean>(false);
  const [zipCodeFilterValue, setZipCodeFilterValue] = useState<string>("");
  const [keyReport, setKeyReport] = useState(0);

  const ref = useRef({
    report: reportRef,
  });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const getInitialFilters = async () => {
    if (
      !initialFiltersArr.length &&
      ref.current.report &&
      Object.keys(ref.current.report).length
    ) {
      try {
        const reportFilters = await ref.current.report.getFilters();
        setInitialFiltersArr(reportFilters);
      } catch (error) {
        console.error(error);
      }
    }
  };


  const handleApplyFilters = async (expanLimit?: string) => {
    if (ref.current.report && Object.keys(ref.current.report).length) {
      handleResetFilters();

      const filtersArr = [];
      const formattedZipCode = zipCodeFilterValue.replaceAll(/[\s-]/g, "");
      const reportProviderId = location.state.providerId;
      let reportTable: string = "";
      if (reportProviderId === 1) reportTable = "physician_table";
      else if (reportProviderId === 2) reportTable = "physician_table"; // Should have his own table when it becomes aviable
      else if (reportProviderId === 3) reportTable = "location_table";
      else if (reportProviderId === 4) reportTable = "taxonomy_table";

      if (zipCodeFilterValue) {
        filtersArr.push({
          $schema: "http://powerbi.com/product/schema#advanced",
          target: {
            table: reportTable,
            column: "postal_code",
          },
          logicalOperator: "And",
          conditions: [
            {
              operator: "Contains",
              value: formattedZipCode || "",
            },
          ],
          filterType: models.FilterType.Advanced,
        });

        try {
          await ref.current.report.updateFilters(
            models.FiltersOperations.Add,
            filtersArr
          );
        } catch (error) {
          console.error(error);
        }
      }
    }
  };

  const handleResetFilters = async () => {
    if (
      initialFiltersArr.length &&
      ref.current.report &&
      Object.keys(ref.current.report).length
    ) {
      try {
        await ref.current.report.updateFilters(
          models.FiltersOperations.ReplaceAll,
          initialFiltersArr
        );
      } catch (error) {
        console.error(error);
      }
    }
  };

  useEffect(() => {
    if (!location.state.addFilter) {
      setKeyReport(keyReport + 1);
    }
  }, [location]);

  useEffect(() => {
    if (reportRef) {
      ref.current.report = reportRef;
      if (!initialFiltersArr.length) {
        getInitialFilters();
      }
    }
  }, [reportRef, initialFiltersArr, getInitialFilters]);

  return (
    <Fragment>
      <Box className={styles.pageContainer} key={keyReport}>
        <Toast
          key="save-template"
          message={
            saveTemplateRequestStatus !== RequestStatus.Failed
              ? "Template saved successfully"
              : "Error saving the template"
          }
          open={showSnackBar}
          variant={
            saveTemplateRequestStatus !== RequestStatus.Failed
              ? ToastVariant.Success
              : ToastVariant.Error
          }
          onClose={() => {
            setShowSnackbar(false);
            dispatch(setSaveTemplateRequestStatus(RequestStatus.Idle));
          }}
        />
        <Box className={styles.graphContainer}>
          <ReportToolbar
            cardId={location.state?.cardId}
            isFiltersMenuOpen={isFiltersMenuOpen}
            isReportLoaded={isReportLoaded}
            isViewMode={isViewMode}
            providerId={location.state.providerId}
            reportPage={reportPage}
            reportState={reportState}
            savedTemplateName={location.state.name}
            setIsFiltersMenuOpen={setIsFiltersMenuOpen}
            setReportPage={setReportPage}
            setShowSnackbar={setShowSnackbar}
          />

          <ReportFiltersMenu
            open={isFiltersMenuOpen}
            zipCodeFilterValue={zipCodeFilterValue}
            handleApplyFilters={handleApplyFilters}
            handleResetFilters={handleResetFilters}
            setZipCodeFilterValue={setZipCodeFilterValue}
          />

          <PowerBIReport
            key={keyReport}
            page={reportPage}
            values={location.state}
            setIsReportLoaded={setIsReportLoaded}
            setReportRef={setReportRef}
            setReportState={setReportState}
          />
        </Box>
      </Box>
    </Fragment>
  );
};

export default Graphs;
